import { useState } from "react";
import { BookingCostSummary } from "./BookingCostSummary";
import { hasPriceInfo, lateCancelNote, type RoomPricing } from "./pricing";

export interface BookingDialogEvent {
  id: number;
  title: string;
  start: string;
  end: string;
  organizerName?: string;
  description?: string;
  isMyBooking?: boolean;
}

export interface BookingDialogProps {
  /** Position relative to viewport */
  x: number;
  y: number;
  onDismiss: () => void;

  /**
   * Booking mode — show time range, title/description fields + Book button.
   *
   * Booking mode needs BOTH ``bookingSlot`` and ``onBook``. The two modes are
   * mutually exclusive and booking mode wins: pass a complete booking slot
   * *and* an ``event`` and the booking form renders while the event is
   * ignored. Pass one mode's props, not both.
   */
  bookingSlot?: { start: string; end: string };
  onBook?: (start: string, end: string, title: string, description: string) => void;
  bookPending?: boolean;

  /** Event mode — show event details + Cancel button. Ignored while booking
   *  mode is active (see ``bookingSlot``). */
  event?: BookingDialogEvent;
  onCancel?: (eventId: number) => void;
  cancelPending?: boolean;

  /** Optional line under the booking time, e.g. the estimated charge of a paid room. */
  priceNote?: (start: string, end: string) => string | null;
  /** Optional warning shown when confirming a cancellation, e.g. a late-cancellation charge. */
  cancelNote?: (event: BookingDialogEvent) => string | null;
  /** The room's pricing (meeting-room charging). A paid room shows its estimated cost and terms above
   *  the Book button, and the late-cancellation warning when confirming a cancel (unless ``cancelNote``
   *  is given). Replaces ``priceNote`` when both are passed. */
  pricing?: RoomPricing | null;
  /** Optional line under the cost, e.g. "will be sent to your company admin for approval" (#401). Shown with or
   *  without pricing — unlike ``priceNote``. */
  bookingNotice?: (start: string, end: string) => string | null;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatDuration(ms: number): string {
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ---------------------------------------------------------------------------
// Shared inline styles — Mantine tokens, so the dialog follows the colour
// scheme instead of only happening to look right on a dark one.
// ---------------------------------------------------------------------------

const fieldStyle: React.CSSProperties = {
  width: "100%",
  padding: "6px 8px",
  background: "var(--mantine-color-default)",
  color: "var(--mantine-color-text)",
  border: "1px solid var(--mantine-color-default-border)",
  borderRadius: 6,
  outline: "none",
  boxSizing: "border-box",
};

const secondaryButtonStyle: React.CSSProperties = {
  fontSize: 13,
  color: "var(--mantine-color-dimmed)",
  background: "var(--mantine-color-default)",
  border: "1px solid var(--mantine-color-default-border)",
  borderRadius: 6,
  padding: "8px 14px",
  cursor: "pointer",
};

export function BookingDialog({
  x, y, onDismiss,
  bookingSlot, onBook, bookPending,
  event, onCancel, cancelPending,
  priceNote, cancelNote, pricing, bookingNotice,
}: BookingDialogProps) {
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [title, setTitle] = useState("Meeting");
  const [description, setDescription] = useState("");

  const winW = typeof window !== "undefined" ? window.innerWidth : 800;
  const winH = typeof window !== "undefined" ? window.innerHeight : 600;
  const left = Math.min(x, winW - 300);
  // A priced room's cost summary makes the booking card taller; clamp against that height.
  const priced = hasPriceInfo(pricing);
  const top = Math.max(10, Math.min(y, winH - (priced ? 420 : 250)));
  const cancelWarning = (ev: BookingDialogEvent) => (cancelNote ? cancelNote(ev) : lateCancelNote(pricing, ev.start));

  // The two modes are exclusive. Booking mode is the deliberate combination —
  // it needs two props that agree — so it wins over a lone ``event``, and it
  // is the mode holding unsaved input (a typed title) that would be discarded
  // if the event view took over. Before this, the two conditionals could both
  // be true and the dialog rendered the book form and the event details
  // stacked inside one card.
  const mode: "book" | "event" | null =
    bookingSlot && onBook ? "book" : event ? "event" : null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onDismiss} style={{ position: "fixed", inset: 0, zIndex: 49 }} />

      {/* Dialog */}
      <div
        style={{
          position: "fixed", left, top, zIndex: 50,
          background: "color-mix(in srgb, var(--mantine-color-body) 95%, transparent)",
          backdropFilter: "blur(8px)",
          border: "1px solid var(--mantine-color-default-border)",
          borderRadius: 10, padding: "14px 18px",
          maxWidth: 300, minWidth: 200,
          boxShadow: "var(--mantine-shadow-xl)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Booking mode */}
        {mode === "book" && bookingSlot && onBook && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mantine-color-text)", marginBottom: 6 }}>
              Book this slot
            </div>
            <div style={{ fontSize: 13, color: "var(--mantine-color-text)", marginBottom: 2 }}>
              {formatTime(bookingSlot.start)} – {formatTime(bookingSlot.end)}
              <span style={{ color: "var(--mantine-color-dimmed)", marginLeft: 6, fontSize: 12 }}>
                {formatDuration(new Date(bookingSlot.end).getTime() - new Date(bookingSlot.start).getTime())}
              </span>
            </div>
            {priced && (
              <div style={{ marginTop: 8 }}>
                <BookingCostSummary pricing={pricing} start={bookingSlot.start} end={bookingSlot.end} compact />
              </div>
            )}
            {!priced && priceNote?.(bookingSlot.start, bookingSlot.end) && (
              <div style={{ fontSize: 12, color: "var(--mantine-color-dimmed)", marginTop: 4 }}>
                {priceNote(bookingSlot.start, bookingSlot.end)}
              </div>
            )}
            {bookingNotice?.(bookingSlot.start, bookingSlot.end) && (
              <div
                data-testid="booking-notice"
                style={{ fontSize: 12, color: "var(--mantine-color-orange-text)", marginTop: 6 }}
              >
                {bookingNotice(bookingSlot.start, bookingSlot.end)}
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title"
                disabled={bookPending}
                style={{ ...fieldStyle, fontSize: 13 }}
              />
            </div>
            <div style={{ marginTop: 6 }}>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description (optional)"
                rows={2}
                disabled={bookPending}
                style={{ ...fieldStyle, fontSize: 12, resize: "vertical" }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button
                onClick={() => onBook(bookingSlot.start, bookingSlot.end, title.trim() || "Meeting", description.trim())}
                disabled={bookPending}
                style={{
                  fontSize: 13, fontWeight: 600,
                  background: "var(--mantine-color-blue-filled)",
                  color: "var(--mantine-color-white)",
                  border: "none", borderRadius: 6, padding: "8px 20px",
                  cursor: bookPending ? "wait" : "pointer",
                  opacity: bookPending ? 0.7 : 1,
                  flex: 1,
                }}
              >
                {bookPending ? "Booking…" : "Book"}
              </button>
              <button onClick={onDismiss} disabled={bookPending} style={secondaryButtonStyle}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Event mode */}
        {mode === "event" && event && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--mantine-color-text)", marginBottom: 4 }}>
              {event.title || "Booked"}
            </div>
            <div style={{ fontSize: 13, color: "var(--mantine-color-dimmed)" }}>
              {formatTime(event.start)} – {formatTime(event.end)}
            </div>
            {event.organizerName && (
              <div style={{ fontSize: 12, color: "var(--mantine-color-dimmed)", marginTop: 2 }}>
                {event.organizerName}
              </div>
            )}
            {event.description && (
              <div style={{
                fontSize: 12, color: "var(--mantine-color-dimmed)", marginTop: 6,
                borderTop: "1px solid var(--mantine-color-default-border)", paddingTop: 6,
              }}>
                {event.description}
              </div>
            )}

            {/* Cancel — only for own future bookings */}
            {event.isMyBooking && onCancel && new Date(event.end) > new Date() && (
              <div style={{
                marginTop: 10, paddingTop: 10,
                borderTop: "1px solid var(--mantine-color-default-border)",
              }}>
                {confirmingCancel ? (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mantine-color-text)", marginBottom: 8 }}>
                      Cancel this booking?
                    </div>
                    {cancelWarning(event) && (
                      <div style={{ fontSize: 12, color: "var(--mantine-color-red-text)", marginBottom: 8 }}>
                        {cancelWarning(event)}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => { onCancel(event.id); onDismiss(); }}
                        disabled={cancelPending}
                        style={{
                          fontSize: 13, fontWeight: 600,
                          background: "var(--mantine-color-red-filled)",
                          color: "var(--mantine-color-white)",
                          border: "none", borderRadius: 6, padding: "8px 16px",
                          cursor: cancelPending ? "wait" : "pointer",
                          opacity: cancelPending ? 0.7 : 1,
                        }}
                      >
                        {cancelPending ? "Cancelling..." : "Yes, cancel"}
                      </button>
                      <button onClick={() => setConfirmingCancel(false)} style={secondaryButtonStyle}>
                        Keep it
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmingCancel(true)}
                    style={{
                      fontSize: 13, fontWeight: 500,
                      color: "var(--mantine-color-white)",
                      background: "var(--mantine-color-red-filled)",
                      border: "none", borderRadius: 6, padding: "8px 16px",
                      cursor: "pointer", width: "100%",
                    }}
                  >
                    Cancel booking
                  </button>
                )}
              </div>
            )}

            {!event.isMyBooking && (
              <div style={{ fontSize: 11, color: "var(--mantine-color-dimmed)", marginTop: 8 }}>
                Not your booking
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
